/**
 * Participant Management Helpers
 * 
 * Handles participant extraction from damage case data and master user management
 */

import { isEmpty } from 'lodash';
import { Participant } from '../types';
import Auth from '../../../utils/Auth';

/**
 * Extract participants from damage case data (similar to ELP logic)
 */
export function extractParticipantsFromDamageCase(damageCase: any): Participant[] {
    const participants: Participant[] = [];

    // Add backend-provided participants array (user_* keys)
    if (Array.isArray(damageCase?.participants)) {
        damageCase.participants.forEach((p: any) => {
            const email = p?.user_email || p?.email;
            const id = p?.user_id || p?.id;
            const label = p?.user_label || p?.label || email;

            if (!email) return;

            const participant: Participant = {
                id,
                email,
                label,
            };

            // Flags based on prefix (without altering email)
            if (email.startsWith('alpc_')) {
                participant.is_alpc = true;
            }
            if (email.startsWith('acm_')) {
                participant.is_acm = true;
            }

            participants.push(participant);
        });
    }

    // Add supplier compound
    if (damageCase?.supplier_compound?.email) {
        participants.push({
            email: damageCase.supplier_compound.email,
            label: damageCase.supplier_compound.name || damageCase.supplier_compound.email,
        });

        // Add responsible email if different
        if (
            damageCase.supplier_compound.responsible_email &&
            damageCase.supplier_compound.responsible_email !== damageCase.supplier_compound.email
        ) {
            participants.push({
                email: damageCase.supplier_compound.responsible_email,
                label: damageCase.supplier_compound.name || damageCase.supplier_compound.email,
            });
        }
    }

    // Add insurance company (Dekra)
    if (damageCase?.insurance_company?.email) {
        participants.push({
            email: damageCase.insurance_company.email,
            label: damageCase.insurance_company.name || damageCase.insurance_company.email,
        });
    }

    // Add admin email (EFL)
    if (damageCase?.admin_email) {
        participants.push({
            email: damageCase.admin_email,
            label: damageCase.admin_email,
        });
    }

    // Add assigned user (EFL)
    if (damageCase?.user?.email) {
        participants.push({
            email: damageCase.user.email,
            label: damageCase.user.label || damageCase.user.name || damageCase.user.email,
        });
    }

    // Add supplier carrier
    if (damageCase?.supplier_carrier?.email) {
        participants.push({
            email: damageCase.supplier_carrier.email,
            label: damageCase.supplier_carrier.name || damageCase.supplier_carrier.email,
        });

        // Add responsible email if different
        if (
            damageCase.supplier_carrier.responsible_email &&
            damageCase.supplier_carrier.responsible_email !== damageCase.supplier_carrier.email
        ) {
            participants.push({
                email: damageCase.supplier_carrier.responsible_email,
                label: damageCase.supplier_carrier.name || damageCase.supplier_carrier.email,
            });
        }
    }

    return participants;
}

/**
 * Get current authenticated user as participant
 * Adds alpc_ prefix to email if it doesn't already have it
 */
export function getCurrentUserAsParticipant(): Participant | null {
    const auth = new Auth();
    const user: any = auth.getUser();

    if (!user || !user.email) {
        return null;
    }

    // Build user label from first_name and last_name, or use name field
    const userLabel = user.name || 
                      (user.first_name && user.last_name 
                         ? `${user.first_name} ${user.last_name}` 
                         : user.email);

    // Add alpc_ prefix to email if it doesn't already have it
    let userEmail = user.email;
    if (!userEmail.startsWith('alpc_')) {
        userEmail = `alpc_${userEmail}`;
    }

    return {
        id: user.id,
        email: userEmail,
        label: userLabel,
    };
}

/**
 * Add master user to participants if it doesn't exist
 * 
 * The master user is the compound user that must always be present
 * Checks by both id and email to ensure uniqueness
 */
export function ensureMasterUser(participants: Participant[], masterUser: Participant | null | undefined): Participant[] {
    if (!masterUser || !masterUser.email || isEmpty(masterUser.email) || !masterUser.id) {
        return participants;
    }

    // Check if master user already exists in participants by both id and email
    const masterExists = participants.some(p => {
        const emailMatch = p.email?.toLowerCase() === masterUser.email.toLowerCase();
        const idMatch = p.id && masterUser.id && String(p.id) === String(masterUser.id);
        return emailMatch || idMatch;
    });

    if (!masterExists) {
        // Add master user at the beginning
        return [masterUser, ...participants];
    }

    return participants;
}

/**
 * Add master user label to ALPC sub-users
 * 
 * When adding sub-users from ALPC, include master user label in their label
 * Checks masterUser.label or masterUser.master_user.label
 */
export function addMasterUserNameToSubUsers(
    participants: Participant[],
    masterUser: Participant | null | undefined
): Participant[] {
    if (!masterUser || !masterUser.email || isEmpty(masterUser.email)) {
        return participants;
    }

    // Get master label from masterUser.label or masterUser.master_user.label
    const masterLabel = masterUser.label || masterUser.master_user?.label;

    if (!masterLabel) {
        return participants;
    }

    return participants.map(participant => {
        // Skip master user itself (check by email and id)
        const isMasterUser = (
            participant.email?.toLowerCase() === masterUser.email.toLowerCase() ||
            (participant.id && masterUser.id && String(participant.id) === String(masterUser.id))
        );

        if (isMasterUser) {
            return participant;
        }

        // Add master label to participant label if not already present
        const hasMasterLabel = participant.label?.includes(masterLabel);

        if (!hasMasterLabel && participant.label) {
            return {
                ...participant,
                label: `${participant.label} (${masterLabel})`,
            };
        }

        return participant;
    });
}

/**
 * Merge participants from multiple sources (Firestore, damage case, current user)
 * Removes duplicates based on email (case-insensitive)
 */
export function mergeParticipants(
    ...participantLists: Participant[][]
): Participant[] {
    const mergedByEmail = new Map<string, Participant>();
    const mergedById = new Map<string, Participant>();

    participantLists.forEach(list => {
        list.forEach(participant => {
            const emailKey = participant.email ? participant.email.toLowerCase() : null;
            const idKey = participant.id ? String(participant.id) : null;

            if (idKey && mergedById.has(idKey)) {
                return;
            }
            if (emailKey && mergedByEmail.has(emailKey)) {
                return;
            }

            if (emailKey) {
                mergedByEmail.set(emailKey, participant);
            }
            if (idKey) {
                mergedById.set(idKey, participant);
            }
        });
    });

    return Array.from(mergedByEmail.values());
}

/**
 * Prepare participants for chat initialization
 * 
 * This function:
 * 1. Extracts participants from damage case
 * 2. Adds current user
 * 3. Ensures master user is present
 * 4. Adds master user name to sub-users
 * 5. Merges with existing Firestore participants
 */
export function prepareChatParticipants(
    damageCase: any,
    firestoreParticipants: Participant[] = [],
    masterUser: Participant | null | undefined = null,
    initialFromProps: Participant[] = []
): Participant[] {
    // Extract from damage case
    const damageCaseParticipants = extractParticipantsFromDamageCase(damageCase);

    // Get current user
    const currentUser = getCurrentUserAsParticipant();
    const currentUserList = currentUser ? [currentUser] : [];

    // Merge all participants
    let participants = mergeParticipants(
        firestoreParticipants,
        damageCaseParticipants,
        currentUserList,
        initialFromProps
    );

    // Ensure master user is present (only if masterUser is provided and valid)
    if (masterUser && masterUser.email && masterUser.id) {
        participants = ensureMasterUser(participants, masterUser);
        // Add master user name to sub-users
        participants = addMasterUserNameToSubUsers(participants, masterUser);
    }

    return participants;
}

